import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  AiAnalyseRequest,
  AiOverrideRequest,
  AiFetchFieldRequest,
  AiIndexCaseRequest,
  AiListResponse,
  AiAnalyseResult,
  AiAnalyseResponse,
  AiEntityResponse,
  AiRegistrarAnalysis,
  AiProceduralBriefing,
  AiChatHistoryEntry,
  AiChatHistoryResponse,
} from '../models/ai.model';
import { environment } from '../../../environments/environment';

/**
 * Client for the FastAPI JusticeFlow AI service.
 *
 * Separate host from the .NET POC API (environment.aiApiUrl) and authenticated
 * with a static x-api-key header. Response shapes are not finalised yet, so
 * reads are typed `unknown` for now — narrow them once payloads are confirmed.
 */
@Injectable({ providedIn: 'root' })
export class AiService {
  private readonly base = `${environment.aiApiUrl}/api/v1/JusticeFlow`;

  constructor(private http: HttpClient) {}

  /** x-api-key header applied to every request */
  private get headers(): HttpHeaders {
    return new HttpHeaders({ 'x-api-key': environment.aiApiKey });
  }

  /** POST /analyse — run the full AI analysis pipeline for a newly filed case */
  analyse(body: AiAnalyseRequest): Observable<AiAnalyseResult> {
    return this.http
      .post<AiAnalyseResponse>(`${this.base}/analyse`, body, {
        headers: this.headers,
      })
      .pipe(map((res) => res.entity));
  }

  /** POST /re-analyse — re-run analysis after a citizen uploads a missing document */
  reAnalyse(body: AiAnalyseRequest): Observable<AiAnalyseResult> {
    return this.http
      .post<AiAnalyseResponse>(`${this.base}/re-analyse`, body, {
        headers: this.headers,
      })
      .pipe(map((res) => res.entity));
  }

  /** POST /override — save a registrar override of an AI field with audit justification */
  override(body: AiOverrideRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.base}/override`, body, {
      headers: this.headers,
    });
  }

  /** POST /fetch-field — fetch a specific AI analysis field for a case */
  fetchField(body: AiFetchFieldRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.base}/fetch-field`, body, {
      headers: this.headers,
    });
  }

  /** POST /index-case — index a case's documents into Qdrant for chat retrieval */
  indexCase(body: AiIndexCaseRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.base}/index-case`, body, {
      headers: this.headers,
    });
  }

  /**
   * POST /chat — stream an AI response for a case.
   *
   * Uses the native fetch API (not HttpClient) because Angular's HttpClient
   * does not expose the streaming ReadableStream. The x-api-key is applied
   * manually since interceptors don't run on fetch calls.
   *
   * @param caseId      Case filing UUID
   * @param caseCode    Case reference string (e.g. LC/2026/00021)
   * @param message     The judge's question
   * @param onChunk     Called with each decoded text chunk as it arrives
   * @param onDone      Called when the stream closes cleanly
   * @param onError     Called if the request or stream fails
   */
  async chatStream(
    caseId: string,
    caseCode: string,
    message: string,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onError: (err: unknown) => void,
  ): Promise<void> {
    try {
      const res = await fetch(`${this.base}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': environment.aiApiKey,
        },
        body: JSON.stringify({ case_id: caseId, case_code: caseCode, message }),
      });

      if (!res.ok || !res.body) {
        onError(
          new Error(`Chat request failed: ${res.status} ${res.statusText}`),
        );
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (chunk) onChunk(chunk);
      }

      onDone();
    } catch (err) {
      onError(err);
    }
  }

  /** GET /chat-history — retrieve prior chat messages for a case */
  getChatHistory(case_id: string, case_code: string): Observable<AiChatHistoryEntry[]> {
    const params = new HttpParams({ fromObject: { case_id, case_code } });
    return this.http
      .get<AiChatHistoryResponse>(`${this.base}/chat-history`, {
        headers: this.headers,
        params,
      })
      .pipe(map((res) => (res.succeeded ? res.entity ?? [] : [])));
  }

  /** GET /classifications — all valid legal case classifications */
  getClassifications(): Observable<string[]> {
    return this.http
      .get<AiListResponse>(`${this.base}/classifications`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.entity ?? []));
  }

  /** GET /divisions — all valid court divisions */
  getDivisions(): Observable<string[]> {
    return this.http
      .get<AiListResponse>(`${this.base}/divisions`, { headers: this.headers })
      .pipe(map((res) => res.entity ?? []));
  }

  /** GET /analysis/{caseId}/registrar — registrar AI view, or null when none exists */
  getRegistrarAnalysis(caseId: string): Observable<AiRegistrarAnalysis | null> {
    return this.http
      .get<AiEntityResponse<AiRegistrarAnalysis>>(
        `${this.base}/analysis/${caseId}/registrar`,
        {
          headers: this.headers,
        },
      )
      .pipe(map((res) => (res.succeeded ? res.entity : null)));
  }

  /** GET /analysis/{caseId}/procedural-briefing — briefing, or null when none exists */
  getProceduralBriefing(
    caseId: string,
  ): Observable<AiProceduralBriefing | null> {
    return this.http
      .get<
        AiEntityResponse<AiProceduralBriefing>
      >(`${this.base}/analysis/${caseId}/procedural-briefing`, { headers: this.headers })
      .pipe(map((res) => (res.succeeded ? res.entity : null)));
  }
}
